import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, TouchableOpacity, Platform, StyleSheet } from 'react-native';
import { palette, FONT } from '../../styles/tourTheme';
import { MAPBOX_ACCESS_TOKEN } from '../../config/mapbox-config';
import {
  PLANNER_ROUTE_COLORS,
  buildPlannerMapModel,
} from '../../utils/calendarRoutingStops';
import {
  fetchLegGeometry,
  kmToMiles,
} from '../../utils/tourPlannerRouting';

function monthStamp(year, month) {
  try {
    return new Date(year, month, 1).toLocaleString('en-US', { month: 'short', year: 'numeric' }).toUpperCase();
  } catch {
    return '';
  }
}

function escHtml(s) {
  return String(s || '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
}

function routeColor(index) {
  return PLANNER_ROUTE_COLORS[Number(index) % PLANNER_ROUTE_COLORS.length];
}

function canvasIsSized(map) {
  try {
    const canvas = map?.getCanvas?.();
    return Boolean(canvas && canvas.clientWidth >= 8 && canvas.clientHeight >= 8);
  } catch {
    return false;
  }
}

function fitTour(mapboxgl, map, coords) {
  if (!map || !coords.length || !canvasIsSized(map)) return;
  if (coords.length === 1) {
    map.easeTo({ center: coords[0], zoom: 8, duration: 0 });
    return;
  }
  const bounds = coords.reduce(
    (b, c) => b.extend(c),
    new mapboxgl.LngLatBounds(coords[0], coords[0])
  );
  map.fitBounds(bounds, { padding: 60, maxZoom: 12, duration: 0 });
}

/**
 * Calendar Map tab — same 2D mercator tour drawing as Tour Planner
 * (all venues, day-colored drive/flight lines, fitted to the tour).
 */
export function MasterTourRoutingMap({
  tour,
  tourName,
  year,
  month,
  isDarkMode,
  accent: _accent,
  onOpenPlanner,
}) {
  const pal = palette(isDarkMode);
  const model = useMemo(() => buildPlannerMapModel(tour), [tour]);
  const modelKey = useMemo(() => JSON.stringify({
    venues: model.venues.map((v) => `${v.id}:${v.latitude}:${v.longitude}:${v.colorIndex}`),
    legs: model.legs.map((l) => `${l.key}:${l.mode}:${l.colorIndex}`),
  }), [model]);
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const mapboxRef = useRef(null);
  const [hostSize, setHostSize] = useState({ w: 0, h: 0 });
  const [mapLoaded, setMapLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [routedMiles, setRoutedMiles] = useState(null);
  const sized = hostSize.w >= 8 && hostSize.h >= 8;
  const canMap = Platform.OS === 'web' && Boolean(MAPBOX_ACCESS_TOKEN) && model.venues.length > 0 && !failed;

  useEffect(() => {
    setRoutedMiles(null);
  }, [modelKey]);

  useEffect(() => {
    if (Platform.OS !== 'web' || !MAPBOX_ACCESS_TOKEN || !canMap || !sized || !containerRef.current) return undefined;
    let cancelled = false;
    setFailed(false);
    setMapLoaded(false);

    (async () => {
      try {
        const mapboxgl = (await import('mapbox-gl')).default;
        await import('mapbox-gl/dist/mapbox-gl.css');
        if (cancelled || !containerRef.current) return;
        mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;
        mapboxRef.current = mapboxgl;
        if (mapRef.current) {
          try { mapRef.current.remove(); } catch { /* ignore */ }
          mapRef.current = null;
        }
        const map = new mapboxgl.Map({
          container: containerRef.current,
          style: isDarkMode ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/mapbox/streets-v11',
          center: [-98.5795, 39.8283],
          zoom: 3,
          minZoom: 3,
          maxZoom: 18,
          projection: 'mercator',
          trackResize: true,
          maxPitch: 85,
          renderWorldCopies: true,
          preserveDrawingBuffer: true,
          customAttribution: 'VRC Tour Planner',
        });
        mapRef.current = map;
        map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
        map.on('load', () => {
          if (cancelled) return;
          try { map.setProjection('mercator'); } catch { /* older GL */ }
          if (!map.getSource('tour-venues')) {
            map.addSource('tour-venues', {
              type: 'geojson',
              data: { type: 'FeatureCollection', features: [] },
            });
          }
          if (!map.getSource('tour-routes')) {
            map.addSource('tour-routes', {
              type: 'geojson',
              data: { type: 'FeatureCollection', features: [] },
            });
          }
          if (!map.getLayer('tour-routes-drive')) {
            map.addLayer({
              id: 'tour-routes-drive',
              type: 'line',
              source: 'tour-routes',
              filter: ['!=', ['get', 'fly'], 1],
              layout: { 'line-join': 'round', 'line-cap': 'round' },
              paint: {
                'line-color': ['get', 'color'],
                'line-width': 3,
                'line-opacity': 0.8,
              },
            });
          }
          if (!map.getLayer('tour-routes-fly')) {
            map.addLayer({
              id: 'tour-routes-fly',
              type: 'line',
              source: 'tour-routes',
              filter: ['==', ['get', 'fly'], 1],
              layout: { 'line-join': 'round', 'line-cap': 'round' },
              paint: {
                'line-color': ['get', 'color'],
                'line-width': 2,
                'line-opacity': 0.8,
                'line-dasharray': [2, 1],
              },
            });
          }
          if (!map.getLayer('tour-venue-shadow')) {
            map.addLayer({
              id: 'tour-venue-shadow',
              type: 'circle',
              source: 'tour-venues',
              paint: {
                'circle-color': 'rgba(0,0,0,0.25)',
                'circle-radius': 12,
                'circle-translate': [1, 1],
                'circle-blur': 0.4,
              },
            });
          }
          if (!map.getLayer('tour-venues')) {
            map.addLayer({
              id: 'tour-venues',
              type: 'circle',
              source: 'tour-venues',
              paint: {
                'circle-color': ['get', 'color'],
                'circle-radius': 8,
                'circle-stroke-width': 3,
                'circle-stroke-color': isDarkMode ? '#131318' : '#EFEFEF',
              },
            });
          }
          if (!map.getLayer('tour-venue-labels')) {
            map.addLayer({
              id: 'tour-venue-labels',
              type: 'symbol',
              source: 'tour-venues',
              minzoom: 5,
              layout: {
                'text-field': ['get', 'name'],
                'text-size': 12,
                'text-offset': [0, 1.15],
                'text-anchor': 'top',
                'text-optional': true,
              },
              paint: {
                'text-color': isDarkMode ? '#f4f4f2' : '#1a1a1a',
                'text-halo-color': isDarkMode ? '#131318' : '#ffffff',
                'text-halo-width': 1.4,
              },
            });
          }
          map.on('click', 'tour-venues', (e) => {
            const f = e.features && e.features[0];
            if (!f) return;
            const city = [f.properties.city, f.properties.state].filter(Boolean).join(', ');
            new mapboxgl.Popup({ offset: 12 })
              .setLngLat(f.geometry.coordinates)
              .setHTML(
                `<div style="font-family:'Space Grotesk',sans-serif;font-size:13px"><b>${escHtml(f.properties.name)}</b>${city ? `<br/>${escHtml(city)}` : ''}</div>`
              )
              .addTo(map);
          });
          map.on('mouseenter', 'tour-venues', () => {
            map.getCanvas().style.cursor = 'pointer';
          });
          map.on('mouseleave', 'tour-venues', () => {
            map.getCanvas().style.cursor = '';
          });
          map.resize();
          setMapLoaded(true);
        });
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
      setMapLoaded(false);
      if (mapRef.current) {
        try { mapRef.current.remove(); } catch { /* ignore */ }
        mapRef.current = null;
      }
    };
  }, [canMap, sized, isDarkMode]);

  useEffect(() => {
    const map = mapRef.current;
    const mapboxgl = mapboxRef.current;
    if (!map || !mapLoaded || !mapboxgl) return undefined;
    const venueSource = map.getSource('tour-venues');
    const routeSource = map.getSource('tour-routes');
    if (!venueSource || !routeSource) return undefined;
    const coords = model.venues.map((v) => [v.longitude, v.latitude]);
    venueSource.setData({
      type: 'FeatureCollection',
      features: model.venues.map((v) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [v.longitude, v.latitude] },
        properties: {
          id: v.id,
          name: v.name,
          city: v.city,
          state: v.state,
          color: routeColor(v.colorIndex),
        },
      })),
    });
    routeSource.setData({ type: 'FeatureCollection', features: [] });
    map.resize();
    fitTour(mapboxgl, map, coords);

    let cancelled = false;
    (async () => {
      const results = await Promise.all(model.legs.map(async (leg) => {
        const start = [leg.from.longitude, leg.from.latitude];
        const end = [leg.to.longitude, leg.to.latitude];
        const route = await fetchLegGeometry(start, end, leg.mode);
        return { leg, route, start, end };
      }));
      if (cancelled || !mapRef.current) return;
      let miles = 0;
      const features = results.map(({ leg, route, start, end }) => {
        if (route?.distance) miles += kmToMiles(route.distance);
        return {
          type: 'Feature',
          properties: {
            fly: leg.mode === 'flight' ? 1 : 0,
            color: routeColor(leg.colorIndex),
          },
          geometry: route?.geometry || { type: 'LineString', coordinates: [start, end] },
        };
      });
      const source = mapRef.current.getSource('tour-routes');
      if (source) source.setData({ type: 'FeatureCollection', features });
      if (features.length) setRoutedMiles(Math.round(miles));
      fitTour(mapboxgl, mapRef.current, coords);
    })();

    return () => {
      cancelled = true;
    };
  }, [mapLoaded, model, modelKey]);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof ResizeObserver === 'undefined') return undefined;
    const node = containerRef.current;
    const mapboxgl = mapboxRef.current;
    if (!node) return undefined;
    let hadSize = canvasIsSized(mapRef.current);
    const ro = new ResizeObserver(() => {
      const map = mapRef.current;
      if (!map) return;
      try { map.resize(); } catch { /* ignore */ }
      const nowSized = canvasIsSized(map);
      if (nowSized && !hadSize) {
        const coords = model.venues.map((v) => [v.longitude, v.latitude]);
        fitTour(mapboxgl, map, coords);
      }
      hadSize = nowSized;
    });
    ro.observe(node);
    return () => ro.disconnect();
  }, [sized, mapLoaded, model.venues]);

  useEffect(() => {
    if (!mapRef.current || !sized) return undefined;
    try { mapRef.current.resize(); } catch { /* ignore */ }
    return undefined;
  }, [hostSize.w, hostSize.h, sized]);

  const bindContainer = useCallback((el) => {
    containerRef.current = el;
  }, []);
  const onHostLayout = (e) => {
    const { width, height } = e?.nativeEvent?.layout || {};
    const w = Math.round(Number(width) || 0);
    const h = Math.round(Number(height) || 0);
    if (w === hostSize.w && h === hostSize.h) return;
    if (w >= 8 && h >= 8) setHostSize({ w, h });
  };

  return (
    <View style={[styles.wrap, { backgroundColor: pal.page }]}>
      {canMap ? (
        <View style={styles.canvas} collapsable={false} onLayout={onHostLayout}>
          {Platform.OS === 'web' ? (
            <div
              ref={bindContainer}
              className="v2-calendar-map"
              style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%' }}
            />
          ) : (
            <View ref={bindContainer} style={StyleSheet.absoluteFill} />
          )}
        </View>
      ) : (
        <View style={[styles.fallback, { backgroundColor: pal.sunk }]}>
          <Text style={{ color: pal.ink, fontSize: 18, fontWeight: '700', marginBottom: 8 }}>
            {tourName || 'Tour map'}
          </Text>
          <Text style={{ color: pal.mute, fontSize: 13, lineHeight: 20, maxWidth: 420 }}>
            {model.venues.length
              ? 'Map could not load. Open Tour Planner for the live map.'
              : (tour
                ? 'No mapped venues on this tour yet. Add venue pins in Tour Planner, then they show here.'
                : 'Pick a tour to plot the routing map.')}
          </Text>
        </View>
      )}
      <View style={[styles.legend, { backgroundColor: pal.surf, borderColor: pal.line }]}>
        <Text style={[styles.legendKicker, { color: pal.mute }]}>
          {monthStamp(year, month)}{tourName ? ` · ${String(tourName).toUpperCase()}` : ''}
        </Text>
        <Text style={[styles.legendMeta, { color: pal.mute }]}>
          {model.venues.length} stop{model.venues.length === 1 ? '' : 's'}
          {routedMiles ? ` · ${routedMiles.toLocaleString()} mi` : ''}
        </Text>
        <TouchableOpacity onPress={onOpenPlanner} style={[styles.legendBtn, { borderColor: pal.ink }]}>
          <Text style={{ color: pal.ink, fontSize: 12, fontWeight: '600' }}>Open in Tour Planner</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: { flex: 1, minHeight: 0, position: 'relative', width: '100%', height: '100%' },
  canvas: {
    flex: 1,
    minHeight: 400,
    minWidth: 300,
    width: '100%',
    height: '100%',
    position: 'relative',
    overflow: 'hidden',
  },
  fallback: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 32 },
  legend: {
    position: 'absolute',
    left: 14,
    bottom: 22,
    zIndex: 5,
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 12,
    maxWidth: 280,
  },
  legendKicker: {
    fontFamily: FONT.sans,
    fontSize: 12.5,
    letterSpacing: 0.8,
    fontWeight: '600',
    marginBottom: 6,
  },
  legendMeta: { fontFamily: FONT.mono, fontSize: 12 },
  legendBtn: {
    marginTop: 10,
    alignSelf: 'flex-start',
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
});

export default MasterTourRoutingMap;
